import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancedGiftForm } from "@/components/gift/EnhancedGiftForm";
import { GoogleSurveyIntegration } from "@/components/gift/GoogleSurveyIntegration";
import { supabase } from "@/integrations/supabase/client";

export default function EditGift() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [gift, setGift] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState<string | null>(null);

  useEffect(() => {
    const loadGift = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('gifts')
          .select(`
            *,
            contacts (email),
            organizations (contact_email)
          `)
          .eq('id', id)
          .single();
          
        if (error) throw error;
        
        setGift(data);
        
        // Get recipient email from contact or organization
        const email = data.contacts?.email || data.organizations?.contact_email;
        setRecipientEmail(email);
      } catch (err: any) {
        setError(err.message || "Failed to load gift");
        toast({
          title: "Error",
          description: "Failed to load gift details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadGift();
  }, [id, toast]);

  const handleSubmit = async (values: any) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      
      // Format dates for API
      const formattedValues = {
        ...values,
        date_sent: values.date_sent 
          ? values.date_sent.toISOString()
          : null,
      };
      
      const { error } = await supabase
        .from('gifts')
        .update(formattedValues)
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Gift updated successfully",
      });
      
      navigate("/gifts");
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update gift",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/gifts">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Gifts</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Gift</h1>
        </div>
        <Card>
          <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !gift) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/gifts">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Gifts</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Gift</h1>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">{error || "Failed to load gift"}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate("/gifts")}
            >
              Back to Gifts
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/gifts">
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Gifts</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit Gift: {gift.name}</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Edit Gift Details</CardTitle>
            </CardHeader>
            <CardContent>
              <EnhancedGiftForm 
                initialValues={gift}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                submitLabel="Update Gift"
              />
            </CardContent>
          </Card>
        </div>
        
        <div className="lg:col-span-1">
          <GoogleSurveyIntegration 
            giftId={id}
            recipientEmail={recipientEmail || undefined}
            onSurveyCreated={(surveyUrl) => {
              toast({
                title: "Survey Created",
                description: "You can now send the feedback survey to the recipient.",
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}